import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { RightOutlined, CheckCircleFilled, LoadingOutlined, ReloadOutlined } from "@ant-design/icons";
import { SbTag, SbButton, SbModal, SbNotification } from "uiCore";
import { Typography } from "antd";
import { getScoreColor } from "Utils/scoreColor.utils";
import { useMatchThresholds } from "Store/useTalentMobilitySettingsStore";
import useApplicationStore, { useHasApplied } from "Store/useApplicationStore";
import { applyTalentMobilityJob } from "Api/apiService";
import { PositionCardStyled, PositionMetaStyled } from "../TalentMobilityEmployeeV2.styles";
import IndexTooltip from "./IndexTooltip";
import { INDEX_FULL_NAMES } from "../talentMobility.constants";

const { Text } = Typography;

// Career-paths position cards — role.id is the position's JD sb_unique_id.
// Apply submits POST /talent-mobility/apply (applicant = the JWT caller);
// the store just caches the success so the tag flips without a refetch.
// While the backend is still computing this position's SMI (isSmiPending —
// the response's is_smi_loading with no smi_score yet), the card is inert:
// Apply is disabled and the row doesn't open the detail view, so nobody
// acts on a role before its match score exists. The refresh button stays
// live — it's how the score catches up.
const PositionCard = ({ opportunityId, role, matchPct, skillsToDevelop, onPath, onRefreshSmi, isRefreshingSmi, isSmiPending = false }) => {
  const navigate = useNavigate();
  const thresholds = useMatchThresholds();
  const applyForRole = useApplicationStore((state) => state.applyForRole);
  const hasApplied = useHasApplied(role.id);
  const [applyModalOpen, setApplyModalOpen] = useState(false);
  const [isApplying, setIsApplying] = useState(false);

  const confirmApply = async () => {
    setIsApplying(true);
    try {
      const response = await applyTalentMobilityJob({ jd_sb_unique_id: role.id });
      applyForRole(role.id);
      SbNotification.success(
        "Application submitted",
        response?.message || `You applied for ${role.title}.`
      );
      setApplyModalOpen(false);
    } catch (error) {
      SbNotification.error(
        "Unable to apply",
        error?.response?.data?.message || "Please try again."
      );
    } finally {
      setIsApplying(false);
    }
  };

  return (
    <>
      <PositionCardStyled
        $onPath={onPath}
        $disabled={isSmiPending}
        onClick={() => {
          if (isSmiPending) return;
          // The card's own data rides along so the detail header renders
          // exactly what was clicked (SkillGapDetailV2 falls back to the
          // aspiration store for deep links without navigation state).
          navigate(`/talent-mobility-employee/opportunity/${opportunityId}`, {
            state: { role, matchPct, skillsToDevelop },
          });
        }}
      >
        <div>
          <div style={{ fontWeight: 600, fontSize: 13 }}>
            {role.title}
            {onPath && role.priority === "High" && (
              <SbTag color="error" style={{ marginLeft: 8 }}>Priority</SbTag>
            )}
            {onPath && role.status === "Open" && (
              <SbTag color="success" style={{ marginLeft: 4 }}>Open</SbTag>
            )}
          </div>
          <div style={{ fontSize: 11.5, color: "#5A6A7A" }}>
            {[role.function, role.location, role.level].filter(Boolean).join(" · ")}
          </div>
        </div>
        <PositionMetaStyled>
          {matchPct != null ? (
            <IndexTooltip index="SMI">
              <SbTag color={getScoreColor(matchPct, thresholds)} style={{ padding: "3px 10px", fontSize: 12.5 }}>
                {matchPct}% SMI
              </SbTag>
            </IndexTooltip>
          ) : (
            // No smi_score in the API response yet — a spinner holds the
            // number's place until one arrives.
            <IndexTooltip index="SMI">
              <SbTag icon={<LoadingOutlined spin />} style={{ padding: "3px 10px", fontSize: 12.5 }}>
                SMI
              </SbTag>
            </IndexTooltip>
          )}
          {onRefreshSmi && (
            // Re-fetches the career-paths response so a pending/stale SMI
            // can catch up without re-saving the aspiration.
            <SbButton
              buttonType="secondary"
              size="small"
              title={`Refresh SMI (${INDEX_FULL_NAMES.SMI})`}
              icon={<ReloadOutlined spin={isRefreshingSmi} />}
              stopPropagation
              onClick={() => onRefreshSmi()}
            />
          )}
          {skillsToDevelop != null && <span>{skillsToDevelop} skill(s) to develop</span>}
          {hasApplied ? (
            <SbTag color="success" icon={<CheckCircleFilled />}>Applied</SbTag>
          ) : (
            <SbButton
              buttonType="primary"
              size="small"
              disabled={isSmiPending}
              title={isSmiPending ? "Available once your SMI score for this role is ready." : undefined}
              onClick={(e) => {
                e.stopPropagation();
                setApplyModalOpen(true);
              }}
            >
              Apply
            </SbButton>
          )}
          <RightOutlined />
        </PositionMetaStyled>
      </PositionCardStyled>

      <SbModal
        open={applyModalOpen}
        title="Apply for this role?"
        onCancel={(e) => {
          e?.stopPropagation?.();
          setApplyModalOpen(false);
        }}
        footer={[
          <SbButton key="cancel" buttonType="secondary" onClick={() => setApplyModalOpen(false)}>
            Cancel
          </SbButton>,
          <SbButton key="apply" buttonType="primary" loading={isApplying} onClick={confirmApply}>
            Apply
          </SbButton>,
        ]}
      >
        <Text>
          You're about to apply for <strong>{role.title}</strong>. Your application will be submitted
          for this open position.
        </Text>
      </SbModal>
    </>
  );
};

export default PositionCard;
